
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import api from '../../api/api';
import { Search, Trash2, UserCheck, UserX, ChevronLeft, ChevronRight, PencilIcon, PlusCircle } from 'lucide-react';
import MultiSelect from '../../components/ui/MultiSelect';

function AddMembershipModal({ open, user, onClose, onSave }) {
    const { isDarkMode } = useTheme();
    const [tierId, setTierId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [tiers, setTiers] = useState([]);
    useEffect(() => {
        if (open) {
            api.get('/membership/admin/tiers').then(res => {
                setTiers(res.data.tiers || []);
            });
        }
    }, [open]);
    if (!open) return null;
    return (
        <div className="modal-overlay">
            <div className={`modal-content text-sm font-medium ${isDarkMode ? 'bg-slate-800 text-slate-100' : 'text-slate-900'}`}>
                <h2 className="text-lg font-bold mb-4">Add Membership</h2>
                <div className="mb-4">
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`}>Tier</label>
                    <select className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`} value={tierId} onChange={e => setTierId(e.target.value)}>
                        <option value="">Select Tier</option>
                        {tiers.map(t => (
                            <option key={t.TierID || t.id} value={t.TierID || t.id}>{t.TierName}</option>
                        ))}
                    </select>
                </div>
                <div className="mb-4">
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`}>Start Date</label>
                    <input type="date" className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : ''}`} value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="mb-4">
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`}>End Date</label>
                    <input type="date" className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : ''}`} value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
                <div className="flex gap-2 justify-end">
                    <button className={`px-3 py-1 rounded ${isDarkMode ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`} onClick={onClose}>Cancel</button>
                    <button className="px-3 py-1 rounded bg-blue-600 text-white" onClick={() => onSave({ tierId, startDate, endDate })} disabled={!tierId || !startDate || !endDate}>Add</button>
                </div>
            </div>
        </div>
    );
}

function EditUserModal({ open, user, onClose, onSave, personaOptions, intentOptions, industryOptions }) {
    const { isDarkMode } = useTheme();
    const [email, setEmail] = useState(user?.EmailAddress || '');
    const [role, setRole] = useState(user?.UserTypeID || 4);
    const [firstName, setFirstName] = useState(user?.FirstName || '');
    const [lastName, setLastName] = useState(user?.LastName || '');
    const [personaId, setPersonaId] = useState(user?.PersonaID || '');
    const [intentId, setIntentId] = useState(user?.IntentID || '');
    const [industryIds, setIndustryIds] = useState((user?.ClientIndustries || []).map(i => i.IndustryID));
    useEffect(() => {
        setEmail(user?.EmailAddress || '');
        setRole(user?.UserTypeID || 4);
        setFirstName(user?.FirstName || '');
        setLastName(user?.LastName || '');
        setPersonaId(user?.PersonaID || '');
        setIntentId(user?.IntentID || '');
        setIndustryIds((user?.ClientIndustries || []).map(i => i.IndustryID));
    }, [user]);
    if (!open) return null;
    const inputClass = `w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : ''}`;
    const selectClass = `select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`;
    const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`;
    return (
        <div className="modal-overlay">
            <div className={`modal-content text-sm font-medium ${isDarkMode ? 'bg-slate-800 text-slate-100' : 'text-slate-900'}`}>
                <h2 className="text-lg font-bold mb-4">Edit User</h2>
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className={labelClass}>First Name</label>
                        <input type="text" className={inputClass} value={firstName} onChange={e => setFirstName(e.target.value)} />
                    </div>
                    <div>
                        <label className={labelClass}>Last Name</label>
                        <input type="text" className={inputClass} value={lastName} onChange={e => setLastName(e.target.value)} />
                    </div>
                </div>
                <div className="mb-4">
                    <label className={labelClass}>Email</label>
                    <input type="email" className={inputClass} value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                <div className="mb-4">
                    <label className={labelClass}>Role</label>
                    <select className={selectClass} value={role} onChange={e => setRole(Number(e.target.value))}>
                        <option value={2}>Admin</option>
                        <option value={4}>Client</option>
                    </select>
                </div>
                <div className="mb-4">
                    <label className={labelClass}>Persona</label>
                    <select className={selectClass} value={personaId} onChange={e => setPersonaId(e.target.value ? Number(e.target.value) : '')}>
                        <option value="">-- None --</option>
                        {personaOptions.map(opt => (
                            <option key={opt.PersonaID || opt.id} value={opt.PersonaID || opt.id}>{opt.PersonaName || opt.name}</option>
                        ))}
                    </select>
                </div>
                <div className="mb-4">
                    <label className={labelClass}>Intent</label>
                    <select className={selectClass} value={intentId} onChange={e => setIntentId(e.target.value ? Number(e.target.value) : '')}>
                        <option value="">-- None --</option>
                        {intentOptions.map(opt => (
                            <option key={opt.IntentID || opt.id} value={opt.IntentID || opt.id}>{opt.IntentName || opt.name}</option>
                        ))}
                    </select>
                </div>
                <div className="mb-4">
                    <label className={labelClass}>Industries</label>
                    <MultiSelect
                        options={industryOptions}
                        selected={industryIds}
                        onChange={setIndustryIds}
                        placeholder="Select one or more industries..."
                        isDarkMode={isDarkMode}
                    />
                </div>
                <div className="flex gap-2 justify-end">
                    <button className={`px-3 py-1 rounded ${isDarkMode ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`} onClick={onClose}>Cancel</button>
                    <button className="px-3 py-1 rounded bg-primary-600 text-white" onClick={() => onSave({ email, role, firstName, lastName, personaId: personaId || null, intentId: intentId || null, industryIds })}>Save</button>
                </div>
            </div>
        </div>
    );
}

export default function UserManagement() {
    const { isDarkMode } = useTheme();
    const [addMembershipOpen, setAddMembershipOpen] = useState(false);
    const [addMembershipUser, setAddMembershipUser] = useState(null);
    const [intentOptions, setIntentOptions] = useState([]);
    const [personaOptions, setPersonaOptions] = useState([]);
    const [industryOptions, setIndustryOptions] = useState([]);
    const [selectedIntent, setSelectedIntent] = useState('');
    const [selectedPersona, setSelectedPersona] = useState('');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const { user } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeOnly, setActiveOnly] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const handleAddMembership = (user) => {
        setAddMembershipUser(user);
        setAddMembershipOpen(true);
    };

    const handleSaveMembership = async ({ tierId, startDate, endDate }) => {
        if (!addMembershipUser) return;
        try {
            await api.post('/membership/admin/add', {
                user_id: addMembershipUser.ClientID,
                tier_id: tierId,
                start_date: startDate,
                end_date: endDate
            });
            setAddMembershipOpen(false);
            setAddMembershipUser(null);
            fetchUsers();
        } catch (err) {
            alert('Failed to add membership');
        }
    };

    const handleEdit = async (user) => {
        // The list row doesn't include ClientIndustries (only the legacy
        // single-value IndustryName for display) - fetch the full detail
        // so the edit modal's multi-select is correctly pre-populated.
        setEditingUser(user);
        setEditModalOpen(true);
        try {
            const res = await api.get(`/admin/users/${user.ClientID}`);
            setEditingUser(res.data);
        } catch (err) {
            // Keep the list-row data as a fallback if the detail fetch fails
        }
    };

    const handleSaveEdit = async ({ email, role, firstName, lastName, personaId, intentId, industryIds }) => {
        if (!editingUser) return;
        try {
            await api.put(`/admin/users/${editingUser.ClientID}`, {
                email,
                user_type_id: role,
                first_name: firstName,
                last_name: lastName,
                persona_id: personaId,
                intent_id: intentId,
                industry_ids: industryIds
            });
            setEditModalOpen(false);
            setEditingUser(null);
            fetchUsers();
        } catch (err) {
            alert('Failed to update user');
        }
    };

    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/userdashboard');
        }
    }, [user, navigate]);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const params = { page, per_page: 20 };
            if (activeOnly) params.active_only = true;
            if (selectedIntent) params.intent = selectedIntent;
            if (selectedPersona) params.persona = selectedPersona;
            if (selectedIndustry) params.industry = selectedIndustry;
            const response = await api.get('/admin/users', { params });
            setUsers(response.data.users || []);
            setTotalPages(response.data.total_pages || 1);
            setTotal(response.data.total || 0);
        } catch (err) {
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    }, [page, activeOnly, selectedIntent, selectedPersona, selectedIndustry]);

    useEffect(() => {
        async function fetchFilters() {
            try {
                const res = await api.get('/meta/lookups');
                const lookups = res.data || {};
                // Convert lookup objects to arrays for filter dropdowns
                setIntentOptions(Object.entries(lookups.intents || {}).map(([name, v]) => ({ id: v.id, name })));
                setPersonaOptions(Object.entries(lookups.personas || {}).map(([name, v]) => ({ id: v.id, name })));
                // "Default_Industry" / "Testingediting&add" are seeded placeholder
                // rows in the Industries table, not real selectable options.
                setIndustryOptions(
                    Object.entries(lookups.industries || {})
                        .filter(([name]) => !['defaultindustry', 'testingeditingadd'].includes(name.replace(/[\s_&]/g, '').toLowerCase()))
                        .map(([name, id]) => ({ id, name }))
                );
            } catch (err) {
                setIntentOptions([]);
                setPersonaOptions([]);
                setIndustryOptions([]);
            }
        }
        fetchFilters();
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const handleToggleStatus = async (userId, currentStatus) => {
        const action = currentStatus ? 'deactivate' : 'reactivate';
        if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
        try {
            await api.patch(`/admin/users/${userId}/status`, { is_active: !currentStatus });
            fetchUsers();
        } catch (err) {
            console.error('Error updating user status:', err);
            alert('Failed to update user status');
        }
    };

    const filteredUsers = users.filter(u => {
        const s = searchTerm.toLowerCase();
        return u.FirstName?.toLowerCase().includes(s) || u.LastName?.toLowerCase().includes(s) || u.EmailAddress?.toLowerCase().includes(s);
    });

    if (loading && users.length === 0) {
        return (
            <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    const selectCls = `border rounded px-4 py-2 text-center ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-300' : 'border-slate-300 text-slate-500'}`;

    return (
        <>
        <EditUserModal
            open={editModalOpen}
            user={editingUser}
            onClose={() => setEditModalOpen(false)}
            onSave={handleSaveEdit}
            personaOptions={personaOptions}
            intentOptions={intentOptions}
            industryOptions={industryOptions}
        />
        <AddMembershipModal
            open={addMembershipOpen}
            user={addMembershipUser}
            onClose={() => setAddMembershipOpen(false)}
            onSave={handleSaveMembership}
        />
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <header className="mb-6">
                    <button onClick={() => navigate('/admin/dashboard')} className="text-primary-600 hover:text-primary-700 mb-4 text-sm font-medium">
                        ← Back to Dashboard
                    </button>
                    <h1 className={`text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>User Management</h1>
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Manage user accounts and permissions</p>
                </header>

                {/* Filters */}
                <div className={`rounded-2xl border shadow-sm p-4 mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-300'}`}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={activeOnly}
                                    onChange={(e) => setActiveOnly(e.target.checked)}
                                    className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Active only</span>
                            </label>
                        </div>
                        {/* Intent Filter */}
                        <div>
                            <select
                                className={`select-styled text-sm font-medium rounded-xl px-4 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                                value={selectedIntent}
                                onChange={e => { setSelectedIntent(e.target.value); setPage(1); }}
                            >
                                <option value="">All Intents</option>
                                {intentOptions.map(opt => (
                                    <option key={opt.IntentID || opt.id} value={opt.IntentID || opt.id}>{opt.IntentName || opt.name}</option>
                                ))}
                            </select>
                        </div>
                        {/* Persona Filter */}
                        <div>
                            <select
                                className={`select-styled text-sm font-medium rounded-xl px-4 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                                value={selectedPersona}
                                onChange={e => { setSelectedPersona(e.target.value); setPage(1); }}
                            >
                                <option value="">All Personas</option>
                                {personaOptions.map(opt => (
                                    <option key={opt.PersonaID || opt.id} value={opt.PersonaID || opt.id}>{opt.PersonaName || opt.name}</option>
                                ))}
                            </select>
                        </div>
                        {/* Industry Filter */}
                        <div>
                            <select
                                className={`select-styled text-sm font-medium rounded-xl px-4 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                                value={selectedIndustry}
                                onChange={e => { setSelectedIndustry(e.target.value); setPage(1); }}
                            >
                                <option value="">All Industries</option>
                                {industryOptions.map(opt => (
                                    <option key={opt.IndustryID || opt.id} value={opt.IndustryID || opt.id}>{opt.IndustryName || opt.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className={`rounded-2xl border shadow-sm overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={`border-b ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                                <tr>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>User</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Email</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Role</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Persona</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Intent</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Industry</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Status</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Created</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                                {filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className={`px-6 py-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            No users found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUsers.map((u) => (
                                        <tr key={u.ClientID} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className={`text-sm font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                                    {u.FirstName} {u.LastName}
                                                </div>
                                                <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                    {u.UserTypeName || 'User'}
                                                </div>
                                            </td>
                                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {u.EmailAddress}
                                            </td>
                                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{u.EmailAddress}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.UserTypeID === 2 ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                                    {u.UserTypeID === 2 ? 'Admin' : 'Client'}
                                                </span>
                                            </td>
                                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {u.PersonaName || '-'}
                                            </td>
                                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {u.IntentName || '-'}
                                            </td>
                                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {u.IndustryName || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.IsActive ? 'bg-primary-100 text-primary-800' : 'bg-red-100 text-red-800'}`}>
                                                    {u.IsActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {u.CreatedDate ? new Date(u.CreatedDate).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleToggleStatus(u.ClientID, u.IsActive)}
                                                        className={`p-2 rounded-lg transition-colors ${u.IsActive ? 'text-red-600 hover:bg-red-50' : 'text-primary-600 hover:bg-primary-50'}`}
                                                        title={u.IsActive ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {u.IsActive ? <UserX size={18} /> : <UserCheck size={18} />}
                                                    </button>
                                                    <button onClick={() => handleEdit(u)} className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors" title="Edit">
                                                        <PencilIcon size={18} />
                                                    </button>
                                                    <button onClick={() => handleAddMembership(u)} className="p-2 rounded-lg text-primary-600 hover:bg-primary-50 transition-colors" title="Add Membership">
                                                        <PlusCircle size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className={`px-6 py-4 border-t flex items-center justify-between ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, total)} of {total} users
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                    Page {page} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
}
